import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Reveal from "@/components/Reveal";

const TITLE = "FAQ — Custom Box Questions Answered | MUBFAR500 Kampala";
const DESCRIPTION =
  "How much do custom boxes cost in Kampala? How long do orders take? MUBFAR500 answers questions on quotes, custom sizes, logo printing and delivery.";

const GROUPS = [
  {
    label: "Quotes & Prices",
    items: [
      {
        q: "How much do your boxes cost?",
        a: "There are no fixed prices. Every box is made to your size, print and quantity, so each order is quoted individually. Add the boxes you want to your quote cart, send the request, and we call or WhatsApp you back with a price per box.",
      },
      {
        q: "Does it cost anything to ask for a quote?",
        a: "No. A quote request is free, and this website never takes payment. You only pay after we have called you with a price and you have confirmed your order.",
      },
      {
        q: "Is there a minimum order?",
        a: "It depends on the box style — each product page shows its own minimum order quantity. We welcome both small orders from individuals and bulk runs for businesses.",
      },
      {
        q: "How do I order custom boxes?",
        a: "Pick your boxes from the shop and add them to the quote cart, enter your measurements, optionally attach your artwork, and send the quote request. We call or WhatsApp you back with a price, you confirm, and we start making your boxes.",
      },
    ],
  },
  {
    label: "Custom Sizes & Printing",
    items: [
      {
        q: "Can you make a box in my exact size?",
        a: "Yes — that is what we do. When you request a quote, you enter your own length, width and height in centimetres for every item, and we cut, print and fold the boxes to those measurements.",
      },
      {
        q: "Can you print my logo or design on the boxes?",
        a: "Yes. You can attach your artwork as a JPG, PNG, WEBP or PDF file when you send your quote request, and we will print it on your boxes. If you do not have artwork yet, describe what you want and we will guide you.",
      },
      {
        q: "What kinds of boxes do you make?",
        a: "We make ten families: gift boxes, pizza boxes, cake boxes, burger boxes, chalk boxes, juice cup holders, round takeaway boxes, paper bags, retail packaging boxes and heavy-duty corrugated cartons — all made to order in Kampala.",
      },
      {
        q: "Can I order as an individual, not a business?",
        a: "Yes. We serve both individuals and businesses — a single special gift box for an event, or thousands of printed boxes for a restaurant, bakery, school or shop.",
      },
    ],
  },
  {
    label: "Timing, Delivery & Contact",
    items: [
      {
        q: "How long does an order take?",
        a: "Production time depends on the box style, the printing and the quantity. We confirm your timeline when we call you back with the quote — before you pay anything — so you always know the date before you commit.",
      },
      {
        q: "Do you deliver?",
        a: "We are based at Muzza Building on Nasser Road in Kampala, Uganda. Delivery or pickup is arranged when we call you with your quote — tell us where you are and we will work out the best way to get your boxes to you.",
      },
      {
        q: "How do I talk to someone directly?",
        a: "Call or WhatsApp us on 0781 928 239 or 0750 928 239, or email Mubirufaruku500@gmail.com. You can also tap the green WhatsApp button on any page and your quote details are pre-filled for you.",
      },
    ],
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: GROUPS.flatMap((g) =>
    g.items.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    }))
  ),
};

export default function FAQ() {
  useEffect(() => {
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href");

    document.title = TITLE;
    if (meta) meta.setAttribute("content", DESCRIPTION);
    if (canonical) canonical.setAttribute("href", "https://mubfar500.com/faq");

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc != null) meta.setAttribute("content", prevDesc);
      if (canonical && prevCanonical != null) canonical.setAttribute("href", prevCanonical);
    };
  }, []);

  return (
    <main className="pt-32 pb-24 min-h-screen" data-testid="faq-page">
      <script type="application/ld+json">{JSON.stringify(FAQ_JSONLD)}</script>
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-mintsoft/90 mb-4">
            Good to Know
          </p>
          <h1 className="font-display font-semibold tracking-tight text-4xl sm:text-6xl text-kraft mb-4">
            Questions, answered
          </h1>
          <p className="max-w-xl text-kraft/60 mb-14">
            Prices, sizes, printing, timing and delivery — everything people usually ask
            before ordering custom boxes in Kampala.
          </p>
        </Reveal>

        {GROUPS.map((group, gi) => (
          <Reveal key={group.label} delay={gi * 0.08}>
            <section className="mb-12" data-testid={`faq-group-${gi}`}>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-mint/80 mb-2">
                {group.label}
              </p>
              <Accordion type="single" collapsible>
                {group.items.map(({ q, a }, i) => (
                  <AccordionItem key={q} value={`${gi}-${i}`} className="border-mint/15">
                    <AccordionTrigger
                      data-testid={`faq-question-${gi}-${i}`}
                      className="font-display text-base sm:text-lg text-kraft hover:text-mint hover:no-underline py-5 [&>svg]:text-mint"
                    >
                      {q}
                    </AccordionTrigger>
                    <AccordionContent className="text-kraft/65 leading-relaxed text-sm sm:text-base max-w-2xl">
                      {a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </Reveal>
        ))}

        <Reveal delay={0.1}>
          <div
            className="mt-16 rounded-2xl border border-mint/20 bg-moss/30 p-8 sm:p-10 text-center"
            data-testid="faq-cta"
          >
            <h2 className="font-display font-semibold text-2xl sm:text-3xl text-kraft mb-3">
              Still wondering about something?
            </h2>
            <p className="text-sm text-kraft/60 max-w-md mx-auto mb-8">
              Send a free quote request and we call you back with a price — or talk to us
              on WhatsApp right now.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/quote"
                data-testid="faq-cta-quote"
                className="inline-flex items-center gap-2 rounded-full bg-mint text-forest font-mono text-[11px] tracking-[0.15em] uppercase font-semibold px-6 h-11 hover:bg-mintsoft transition-colors"
              >
                Start a quote <ArrowRight size={14} />
              </Link>
              <a
                href="https://wa.me/256781928239?text=Hello%20MUBFAR500%2C%20I%20have%20a%20question%20about%20custom%20boxes."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="faq-cta-whatsapp"
                className="inline-flex items-center gap-2 rounded-full border border-mint/40 text-mint font-mono text-[11px] tracking-[0.15em] uppercase px-6 h-11 hover:bg-mint/10 transition-colors"
              >
                <MessageCircle size={14} /> WhatsApp us
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
